// Textos do botão "ajuda" de cada seção do formulário do plano de trabalho

export interface AjudaSecao {
  title: string;
  description: string;
  tips: string[];
}

export const helpContent: Record<string, AjudaSecao> = {
  'info-emenda': {
    title: 'Informações da Emenda Parlamentar',
    description: 'Registre os dados basilares da emenda parlamentar que você deseja utilizar neste plano de trabalho. Todas as informações devem corresponder ao documento oficial da emenda no Congresso Nacional.',
    tips: [
      'Parlamentar: Nome completo (ex: João da Silva, Senador). Busque informações no site da Câmara/Senado',
      'Número da Emenda: Número oficial e ano (ex: 123/2026). Consulte o documento original ou correspondência da emenda',
      'Valor Total: Valor em reais. Digite apenas números (ex: 500000 para R$ 500.000,00)',
      'Programa: Selecione o programa alinhado às ações (Atenção Básica, Urgência, Serviços Especializados, etc)',
      'Importante: Os dados devem estar alinhados com a Portaria de Emendas Parlamentares 2026'
    ]
  },
  'beneficiario': {
    title: 'Dados do Beneficiário - Identificação da Instituição',
    description: 'Identifique com precisão a instituição que será beneficiada. Este campo é crítico para auditoria e conformidade regulatória.',
    tips: [
      'Nome da Instituição: Nome completo e oficial do estabelecimento (ex: Hospital Municipal São João da Boa Vista)',
      'CNES: Código Nacional com 8 dígitos (obtém em: saude.sp.gov.br ou no CNES Nacional). Verifique a vigência',
      'CNPJ: 14 dígitos sem formatação (ex: 12345678901234). Se PJ, valide na Receita Federal',
      'E-mail Institucional: Utilize e-mail corporativo quando disponível. Ex: contato@hospital.sp.gov.br',
      'Telefone com DDD: Use formato completo com DDD (ex: 11999887766). Incluir ramal se houver',
      'Revisão: Verifique todas as informações antes de salvar. Erros aqui afetam toda a auditoria'
    ]
  },
  'alinhamento': {
    title: 'Alinhamento Estratégico com Diretrizes SES-SP',
    description: 'Conecte seu plano às diretrizes estratégicas da Secretaria de Estado da Saúde. Este alinhamento é obrigatório para aprovação e conformidade.',
    tips: [
      'Diretriz: Escolha a diretriz-mãe que melhor se alinha (ex: "Reafirmar o SUS como política de Estado"). Leia a descrição completa',
      'Objetivo Estratégico: Selecione um objetivo dentro da diretriz (ex: "Garantir a gestão bipartite com pactuação em CIB")',
      'Metas Relacionadas: Marque as metas que sua emenda ajudará a cumprir. Pode selecionar múltiplas metas',
      'Justificativa: Na seção final, explique COMO suas ações contribuem para atingir as metas selecionadas',
      'Exemplos Práticos: Emenda para consultas → pode contribuir para "Garantir o acesso oportuno à atenção integral"'
    ]
  },
  'metas-quantitativas': {
    title: 'Metas Quantitativas - Ações e Alocação Financeira',
    description: 'Defina quais ações/serviços serão realizados e quanto de cada emenda será destinado a cada ação. O sistema valida automaticamente o total.',
    tips: [
      'Grupo de Ação: Selecione por tipo (Consultas Especializadas, Procedimentos, Medicamentos, Capacitação, etc)',
      'Ação Específica: Escolha a ação detalhada dentro do grupo (ex: "Consulta de Oftalmologia" dentro de Consultas)',
      'Valor: Informe o valor em reais para essa ação. Ex: 50000 para R$ 50.000,00',
      'Múltiplas Ações: Pode adicionar várias ações. A soma deve ser ≤ valor total da emenda',
      'Validação: O sistema avisa se houver divergências. Releia as ações se o total não bater',
      'Dica: Seja específico nas ações. "Consulta" é vago. "Consulta Oftalmológica em Unidade de Referência" é mais claro'
    ]
  },
  'metas-qualitativas': {
    title: 'Indicadores Qualitativos - Monitoramento e Qualidade (Opcional)',
    description: 'Defina os indicadores que rastrearão a qualidade da execução. Estes servem para monitoramento contínuo durante o ano. Campo opcional.',
    tips: [
      'Indicador de Qualidade: Selecione métricas relevantes (ex: "% de pacientes resolvidos na 1ª consulta", "Satisfação em atendimento")',
      'Valor Alvo: Meta percentual (ex: 85% para resolução na 1ª consulta) ou quantidade (ex: 500 pacientes atendidos)',
      'Monitoramento Contínuo: Esses indicadores serão consultados em relatórios mensais/trimestrais de execução',
      'Múltiplos Indicadores: Recomenda-se 3-5 indicadores. Muito poucos não acompanham qualidade, muitos são onerosos',
      'Legislação: Revise indicadores em literais da Portaria SES. Alguns são mandatórios conforme tipo de ação'
    ]
  },
  'execucao-financeira': {
    title: 'Execução Financeira - Classificação de Despesas',
    description: 'Classifique os gastos conforme as Naturezas de Despesa da legislação de Saúde. Isto é obrigatório para auditoria e conformidade fiscal.',
    tips: [
      'Natureza de Despesa: Classifique por tipo formal (Pessoal, Custeio, Investimento, Transferências, etc)',
      'Pessoal: Salários, encargos e benefícios de servidores contratados para as ações (ex: médicos contratados)',
      'Custeio: Medicamentos, materiais, combustível, energia, diárias, passagens (custos operacionais)',
      'Investimento: Equipamentos, construção, reforma com vida útil > 1 ano (quando permitido em legislação)',
      'Total Convergente: A SOMA de todas as naturezas deve igualar exatamente o valor total da emenda',
      'Revisão Fiscal: Erros aqui causam rejeição em auditoria. Dupla-checagem com setor financeiro é recomendada',
      'Legislação: Siga rigorosamente a Instrução Normativa SES e Decretos sobre Execução Orçamentária 2026'
    ]
  },
  'finalizacao': {
    title: 'Finalização - Justificativa Técnica e Responsável',
    description: 'Complete o plano com uma justificativa técnica sólida e identifique o responsável pela assinatura. Esta é a etapa final antes da submissão.',
    tips: [
      'Responsável pela Assinatura: Nome completo e documento (CPF/cargo). Deve ser responsável pela unidade ou autoridade técnica',
      'Justificativa Técnica: Parágrafo detalhado explicando POR QUE escolheu essa alocação (contexto epidemiológico, deficiências, diagnóstico situacional)',
      'Alinhamento Estratégico: Reafirme como as ações contribuem para as diretrizes e metas estratégicas SES-SP',
      'Legislação Pertinente: Cite portarias, decretos e políticas relevantes (ex: Portaria SES 234/2025, Decreto 67.435/2023)',
      'Realismo: Justificativas genéricas são rejeitadas. Base-se em dados epidemiológicos, demanda reprimida, estudos de viabilidade',
      'Revisão Final: Releia TODO o plano (seções 1-7) antes de salvar. Erros em justificativa afetam aprovação',
      'Assinatura: Será necessária assinatura digital ou manuscrita no PDF gerado. Responsável deve estar disponível'
    ]
  }
};
